// `@dutchie-pro-partners/theme-registry@0.0.5` declares peer `@dutchiesdk/ecommerce-extensions-sdk`
// `>=0.48.0 <0.49.0` (matching installed `^0.48.0`). Because shipped registry ESM bundles import
// `@dutchiesdk/ecommerce-extensions-sdk` (including `DataBridgeVersion`) as a runtime external from
// the host, `remoteComponentIsValid` compares the host's `DataBridgeVersion` against itself for
// registry themes — verify runtime ABI compatibility on any future SDK bump beyond `<0.49.0`.
import { getTheme } from '@dutchie-pro-partners/theme-registry';

type ThemeModule = Record<string, unknown>;
type ThemeModuleWithDefault = ThemeModule & { default?: ThemeModule };

const themeModuleCache = new Map<string, ThemeModule>();
const inFlightLoads = new Map<string, Promise<ThemeModule | null>>();
const failedLoadPromises = new WeakSet<Promise<ThemeModule | null>>();

export function didThemeLoadFail(promise: Promise<ThemeModule | null>): boolean {
  return failedLoadPromises.has(promise);
}

const ADMIN_CUSTOMER_THEMES_SCOPE = '@dutchie/customer-themes';
const REGISTRY_CUSTOMER_THEMES_SCOPE = 'customer_themes';

// Admin and CMS store '@dutchie/customer-themes' in Mongo, while theme-registry keys by 'customer_themes'.
export function normalizeThemeScope(scope: string): string {
  return scope === ADMIN_CUSTOMER_THEMES_SCOPE ? REGISTRY_CUSTOMER_THEMES_SCOPE : scope;
}

export function getThemeCacheKey(scope: string, themeKey: string): string {
  return `${normalizeThemeScope(scope)}:${themeKey}`;
}

export function getLoadedTheme(scope: string, themeKey: string): ThemeModule | undefined {
  return themeModuleCache.get(getThemeCacheKey(scope, themeKey));
}

export function setLoadedTheme(scope: string, themeKey: string, themeModule: ThemeModule): void {
  const candidate = themeModule as ThemeModuleWithDefault;
  const normalized = candidate.default ?? themeModule;
  themeModuleCache.set(getThemeCacheKey(scope, themeKey), normalized);
}

export function hasLoadedTheme(scope: string, themeKey: string): boolean {
  return themeModuleCache.has(getThemeCacheKey(scope, themeKey));
}

export function clearThemeCache(): void {
  themeModuleCache.clear();
  inFlightLoads.clear();
}

export function abandonInFlightLoad(scope: string, themeKey: string, promise: Promise<ThemeModule | null>): void {
  const cacheKey = getThemeCacheKey(scope, themeKey);
  if (inFlightLoads.get(cacheKey) === promise) {
    inFlightLoads.delete(cacheKey);
  }
}

// Not `async`: callers must receive the exact promise stored in inFlightLoads so that
// abandonInFlightLoad's identity check can match it.
export function loadTheme(
  scope: string,
  themeKey: string,
  customLoader?: () => Promise<ThemeModule | null>
): Promise<ThemeModule | null> {
  const normalizedScope = normalizeThemeScope(scope);
  const cacheKey = getThemeCacheKey(normalizedScope, themeKey);
  if (themeModuleCache.has(cacheKey)) {
    return Promise.resolve(themeModuleCache.get(cacheKey) ?? null);
  }

  const inFlight = inFlightLoads.get(cacheKey);
  if (inFlight) {
    return inFlight;
  }

  let promise: Promise<ThemeModule | null> | undefined;
  const state = { syncFailed: false };
  const markFailed = (): void => {
    if (promise) {
      failedLoadPromises.add(promise);
    } else {
      state.syncFailed = true;
    }
  };
  const cacheLoadedModule = (mod: ThemeModule | null): ThemeModule | null => {
    if (!mod || inFlightLoads.get(cacheKey) !== promise) {
      markFailed();
      return null;
    }
    setLoadedTheme(normalizedScope, themeKey, mod);
    return getLoadedTheme(normalizedScope, themeKey) ?? null;
  };
  const executeLoad = async (): Promise<ThemeModule | null> => {
    try {
      if (customLoader) {
        return cacheLoadedModule(await customLoader());
      }

      const record = getTheme(normalizedScope, themeKey);
      if (record && typeof record.load === 'function') {
        const isSsrReady = (record as { ssrReady?: boolean }).ssrReady;
        if (typeof window === 'undefined' && !isSsrReady) {
          return null;
        }
        return cacheLoadedModule(await record.load());
      }
    } catch (error) {
      console.warn(`[theme-cache] Failed to load theme "${cacheKey}":`, error);
      markFailed();
      return null;
    }

    return null;
  };
  promise = executeLoad();
  if (state.syncFailed) {
    failedLoadPromises.add(promise);
  }

  inFlightLoads.set(cacheKey, promise);

  void promise.finally(() => {
    if (inFlightLoads.get(cacheKey) === promise) {
      inFlightLoads.delete(cacheKey);
    }
  });

  return promise;
}
